import IconButton from "@/components/atoms/IconButton";
import AuthCardLayout from "@/components/molecules/AuthCardLayout";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL_MESSAGE,
  INVALID_FORM_MESSAGE,
} from "@/constants/validatorMessages";
import { ILogin } from "@/interfaces/IAuth";
import { useLoginMutation } from "@/services/auth";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const LoginView: FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILogin>();
  const [mutateAsync] = useLoginMutation();
  const [_, setCookie] = useCookies(["te_co"]);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onSubmit: SubmitHandler<ILogin> = async (loginData) => {
    await toast.promise(mutateAsync(loginData).unwrap(), {
      pending: "Authentification en cours...",
      success: {
        render({ data }) {
          setCookie("te_co", data?.token, { path: "/" });
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              C&apos;est un plaisir de vous revoir{" "}
              <b>{data?.user.firstname} 🤩</b>
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 401) {
            return "Votre mot de passe ou votre adresse e-mail est incorrecte.";
          } else if (data?.status === 422) {
            return "Veuillez vérifier vos informations.";
          } else if (data?.status === 403) {
            return "Votre compte n'est pas encore activé.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<ILogin> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <AuthCardLayout
      leftSideContent={
        <p className="text-white">
          Vous n&apos;avez pas de compte ?
          <br />
          <Link
            className="text-white text-decoration-underline"
            to="/auth/register"
          >
            Faire une demande
          </Link>
        </p>
      }
    >
      <h3>Connexion</h3>
      <Form onSubmit={handleSubmit(onSubmit, onError)} className="mt-4">
        <Form.Group className="mb-3">
          <Form.Label>Adresse email :</Form.Label>
          <Form.Control
            {...register("email", {
              required: "Votre adresse-email est requise",
              pattern: {
                value: EMAIL_REGEX,
                message: INVALID_EMAIL_MESSAGE,
              },
            })}
            isInvalid={!!errors.email}
            type="email"
          />
          <Form.Control.Feedback type="invalid">
            {errors.email?.message}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Mot de passe :</Form.Label>

          <InputGroup>
            <Form.Control
              {...register("password", {
                required: "Votre mot de passe est requis",
              })}
              isInvalid={!!errors.password}
              type={showPassword ? "text" : "password"}
            />
            <InputGroup.Text
              as={IconButton}
              variant="outline-secondary"
              icon={showPassword ? faEyeSlash : faEye}
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle-button"
            />
            <Form.Control.Feedback type="invalid">
              {errors.password?.message}
            </Form.Control.Feedback>
          </InputGroup>
        </Form.Group>
        <Row className="justify-content-between align-items-center">
          <Col xs="auto"></Col>
          <Col xs="auto">
            <Link className="fs--1 mb-0" to="/auth/forgotPassword">
              Mot de passe oublié ?
            </Link>
          </Col>
        </Row>
        <Button type="submit" variant="primary" className="mt-3 w-100">
          Se connecter
        </Button>
        {/* <Divider className="mt-4">ou se connecter avec</Divider>

      <SocialAuthButtons /> */}
      </Form>
      <nav aria-label="Informations sur Formalys" className="d-flex flex-wrap gap-3 mt-4 small">
        <a href="/presentation.html">Présentation</a>
        <a href="/confidentialite.html">Confidentialité</a>
        <a href="/conditions-utilisation.html">Conditions d’utilisation</a>
      </nav>
    </AuthCardLayout>
  );
};

export default LoginView;
